import { Link, useLocation, useSearch } from "wouter";
import { Home, LayoutGrid, Plus, MessageSquare, User } from "lucide-react";
import { useRef, useState } from "react";
import { PostMenu } from "./PostMenu";
import { useAuth } from "../lib/auth";
import { useUnreadMessages } from "../lib/useUnreadMessages";
import { useI18n } from "../lib/i18n";

/**
 * Phone/tablet bottom tabs (hidden on lg+). The centered icon-only Post
 * button opens the Post menu; desktop navigation remains in Header.
 */
export function BottomNav() {
  const [loc] = useLocation();
  const search = useSearch();
  const [post, setPost] = useState(false);
  const postButtonRef = useRef<HTMLButtonElement>(null);
  const { user } = useAuth();
  const unreadMsgs = useUnreadMessages();
  const { t } = useI18n();

  const is = (path: string) => (path === "/" ? loc === "/" : loc.startsWith(path));
  const accountHref = user ? "/dashboard" : "/login";
  const tab = (active: boolean) =>
    `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold ${active ? "text-brand-red" : "text-mut"}`;
  const isAccountSubpage = loc === "/dashboard" && Boolean(new URLSearchParams(search).get("view"));

  if (isAccountSubpage) return null;

  return (
    <>
      {/* Keep this in sync with the fixed bar on every mobile platform. */}
      <div className="bottom-nav-spacer lg:hidden" aria-hidden="true" />
      <nav
        className="bottom-nav lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-line flex items-stretch"
        aria-label="Primary"
      >
        <Link href="/" className={tab(is("/"))}>
          <Home className="w-[22px] h-[22px]" strokeWidth={1.8} /> Home
        </Link>
        <Link href="/marketplace" className={tab(is("/marketplace"))}>
          <LayoutGrid className="w-[22px] h-[22px]" strokeWidth={1.8} /> Market
        </Link>

        <div className="flex flex-1 items-center justify-center">
          {/* PostMenu paints the foreground plus; opacity preserves focus here. */}
          <button
            ref={postButtonRef}
            type="button"
            onClick={() => setPost(true)}
            aria-label={t("action.post")}
            className={`grid h-12 w-12 place-items-center rounded-full bg-brand-red text-white ${post ? "opacity-0 transition-none" : "transition-transform active:scale-95"}`}
          >
            <Plus className="h-6 w-6" strokeWidth={2} />
          </button>
        </div>

        <Link href="/messages" className={`${tab(is("/messages"))} relative`}>
          <span className="relative">
            <MessageSquare className="w-[22px] h-[22px]" strokeWidth={1.8} />
            {unreadMsgs > 0 && <span className="absolute -top-1 -right-2 min-w-[15px] h-[15px] px-1 rounded-full bg-brand-red text-white text-[9px] font-bold leading-none grid place-items-center">{unreadMsgs > 9 ? "9+" : unreadMsgs}</span>}
          </span>
          {t("nav.messages")}
        </Link>
        <Link href={accountHref} className={tab(is("/dashboard"))}>
          <User className="w-[22px] h-[22px]" strokeWidth={1.8} /> Account
        </Link>
      </nav>
      <PostMenu open={post} onClose={() => setPost(false)} triggerRef={postButtonRef} />
    </>
  );
}
